import { useState, useEffect, useRef } from "react";
import stats from "../data/stats.json";
import { useScrollReveal } from "../hooks/useScrollReveal";

const iconPaths: Record<string, JSX.Element> = {
  TrophyIcon: (
    <svg className="w-6 h-6 md:w-7 md:h-7" fill="currentColor" viewBox="0 0 24 24">
      <path fillRule="evenodd" d="M5.166 2.621v.858c-1.035.148-2.059.33-3.071.543a.75.75 0 00-.584.859 6.753 6.753 0 006.138 5.6 6.73 6.73 0 002.743 1.346A6.707 6.707 0 019.279 15H8.54c-1.036 0-1.875.84-1.875 1.875V19.5h-.75a.75.75 0 000 1.5h12.17a.75.75 0 000-1.5h-.75v-2.625c0-1.036-.84-1.875-1.875-1.875h-.739a6.707 6.707 0 01-1.112-3.173 6.73 6.73 0 002.743-1.347 6.753 6.753 0 006.139-5.6.75.75 0 00-.585-.858 47.077 47.077 0 00-3.07-.543V2.62a.75.75 0 00-.658-.744 49.22 49.22 0 00-6.093-.377c-2.063 0-4.096.128-6.093.377a.75.75 0 00-.657.744zm0 2.629c0 3.494 1.188 6.686 3.255 9.236a5.25 5.25 0 01-3.062 0c2.066-2.55 3.255-5.742 3.255-9.236V4.12a49.09 49.09 0 015.743.368c.56.188.966.687.937 1.268a48.715 48.715 0 00-3.555.347 48.69 48.69 0 00-3.555-.347.75.75 0 01.937-1.268A49.12 49.12 0 015.166 4.75z" clipRule="evenodd" />
    </svg>
  ),
  UsersIcon: (
    <svg className="w-6 h-6 md:w-7 md:h-7" fill="currentColor" viewBox="0 0 24 24">
      <path d="M4.5 6.375a4.125 4.125 0 118.25 0 4.125 4.125 0 01-8.25 0zM14.25 8.625a3.375 3.375 0 116.75 0 3.375 3.375 0 01-6.75 0zM1.5 19.125a7.125 7.125 0 0114.25 0v.003l-.001.119a.75.75 0 01-.363.63 13.067 13.067 0 01-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 01-.364-.63l-.001-.122zM17.25 19.128l-.001.144a2.25 2.25 0 01-.233.96 10.088 10.088 0 005.06-1.01.75.75 0 00.42-.643 4.875 4.875 0 00-6.957-4.611 8.586 8.586 0 011.71 5.157v.003z" />
    </svg>
  ),
  StarIcon: (
    <svg className="w-6 h-6 md:w-7 md:h-7" fill="currentColor" viewBox="0 0 24 24">
      <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
    </svg>
  ),
  ClockIcon: (
    <svg className="w-6 h-6 md:w-7 md:h-7" fill="currentColor" viewBox="0 0 24 24">
      <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z" clipRule="evenodd" />
    </svg>
  ),
};

const StatsCard = () => {
  const [animatedValues, setAnimatedValues] = useState(stats.map(() => 0));
  const sectionRef = useRef<HTMLDivElement>(null);
  const { ref: bannerRef, isVisible: bannerVisible } = useScrollReveal({ threshold: 0.3 });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (hasAnimated.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          stats.forEach((stat, index) => {
            const targetValue = parseInt(stat.value.replace(/\D/g, ""));
            const duration = 2000;
            const steps = 60;
            const increment = targetValue / steps;
            let current = 0;

            const timer = setInterval(() => {
              current += increment;
              if (current >= targetValue) {
                current = targetValue;
                clearInterval(timer);
              }
              setAnimatedValues((prev) => {
                const newValues = [...prev];
                newValues[index] = Math.floor(current);
                return newValues;
              });
            }, duration / steps);
          });
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Stats Banner Section */}
      <div ref={sectionRef}>
        <div
          ref={bannerRef}
          className={`relative py-16 md:py-20 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 overflow-hidden transition-all duration-700 ${
            bannerVisible ? "opacity-100" : "opacity-0"
          }`}
        >
          {/* Decorative Pattern */}
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <pattern id="dots" width="8" height="8" patternUnits="userSpaceOnUse">
                <circle cx="4" cy="4" r="1" fill="white" />
              </pattern>
              <rect width="100" height="100" fill="url(#dots)" />
            </svg>
          </div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-black/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-semibold uppercase tracking-wider mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-200" />
                Our Track Record
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-3 sm:mb-4 gowun-batang">
                Numbers That Inspire
              </h2>
              <p className="text-orange-100 text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-2 sm:px-0">
                A legacy of excellence built through dedication, discipline, and an unwavering commitment to nurturing champions
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {stats.map((stat, index) => {
                const IconComponent = iconPaths[(stat as any).icon as string] || iconPaths.TrophyIcon;
                return (
                  <div
                    key={index}
                    className={`group relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl md:rounded-2xl p-4 sm:p-5 md:p-7 text-center hover:bg-white/15 transition-all duration-500 ${
                      bannerVisible ? "animate-fadeInUp" : "opacity-0"
                    }`}
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-white/15 text-white mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300">
                      {IconComponent}
                    </div>
                    <div className="mb-2">
                      <span className="text-2xl sm:text-3xl md:text-5xl font-bold text-white block tracking-tight">
                        {stat.value.includes("+")
                          ? `${animatedValues[index]}+`
                          : animatedValues[index]}
                      </span>
                      <span className="text-xs md:text-sm text-orange-100 font-medium uppercase tracking-wider">
                        {stat.label}
                      </span>
                    </div>
                    <p className="text-orange-200/70 text-xs md:text-sm hidden md:block">
                      {stat.description}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Bottom CTA */}
            <div className="text-center mt-12">
              <button
                onClick={() => {
                  const el = document.getElementById("achievements");
                  if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
                }}
                className="bg-white text-orange-600 px-8 py-3.5 rounded-xl font-semibold text-sm md:text-base shadow-xl shadow-orange-700/30 hover:shadow-orange-700/50 hover:bg-orange-50 transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center gap-2"
              >
                Explore Our Achievements
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StatsCard;
